// src/pages/Firms.js
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as XLSX from 'xlsx'
import api from '../lib/api'
import { useRoleOptions } from '../lib/roles'
import MergeModal from '../components/MergeModal'
import EventsAttended from '../components/EventsAttended'
import VenturesLinked from '../components/VenturesLinked'
import OwnerMultiField from '../components/OwnerMultiField'

// ── Constants ─────────────────────────────────────────────────────────────


const FIRM_TYPE_BADGE = {
  'GP Client':       'badge-green',
  'LP / Investor':   'badge-blue',
  'Partner Firm':    'badge-purple',
  'Service Provider':'badge-gray',
  'Other':           'badge-gray',
}

const CONTACT_TYPE_BADGE = {
  'Investor': 'badge-blue', 'GP': 'badge-green',
  'RGA Partner': 'badge-purple', 'Service Provider': 'badge-gray', 'RGA Team': 'badge-yellow', 'Former RGA Team': 'badge-gray',
}

const TAG_COLORS = ['badge-blue','badge-green','badge-purple','badge-yellow','badge-red','badge-gray']
function tagColor(t) { return TAG_COLORS[Math.abs([...t].reduce((a,c) => a + c.charCodeAt(0), 0)) % TAG_COLORS.length] }

const AUM_RANGES = [
  { label: '< $100M',       test: v => v != null && v < 100e6 },
  { label: '$100M-$500M',   test: v => v != null && v >= 100e6 && v <= 500e6 },
  { label: '$500M-$1B',     test: v => v != null && v > 500e6 && v <= 1e9 },
  { label: '$1B+',          test: v => v != null && v > 1e9 },
]

// Investor Subtype options are served by the API from firm_subtype_options —
// never hard-code them here, or the dropdown and the DB drift apart.
const UNCLASSIFIED = '(Unclassified)'

// Subtypes that must name the family or principal behind them (Matt, 2026-10-01).
// Mirrors FAMILY_SUBTYPES in src/lib/familyOffice.js; the server's needs_family
// flag is the source of truth for display, this list only drives the form.
const FAMILY_SUBTYPES = ['Family Office', 'Multi-Family Office']

function MissingFamilyBadge({ size = 10 }) {
  return (
    <span className="badge badge-yellow" style={{ fontSize: size }}
      title="No family or principal listed for this family office">
      Missing family
    </span>
  )
}

// Chip input for principal families. Enter or comma commits a name; order is
// kept (founding family first), duplicates are ignored, Backspace on an empty
// input removes the last chip.
function FamilyChipsInput({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState('')

  function commit(text = draft) {
    const name = text.replace(/,$/, '').trim()
    if (name && !value.some(v => v.toLowerCase() === name.toLowerCase())) onChange([...value, name])
    setDraft('')
  }

  return (
    <div className="input" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
      minHeight: 36, height: 'auto', padding: '4px 8px' }}>
      {value.map((v, i) => (
        <span key={v} className="badge badge-gray" style={{ fontSize: 11, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {v}{i === 0 && value.length > 1 ? ' (founding)' : ''}
          <button type="button" aria-label={`Remove ${v}`}
            onClick={() => onChange(value.filter(x => x !== v))}
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0,
              color: 'var(--text-muted)', fontSize: 13, lineHeight: 1 }}>×</button>
        </span>
      ))}
      <input value={draft} placeholder={value.length ? '' : placeholder}
        onChange={e => e.target.value.endsWith(',') ? commit(e.target.value) : setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); commit() }
          else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1))
        }}
        onBlur={() => commit()}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent',
          color: 'inherit', font: 'inherit', fontSize: 13 }} />
    </div>
  )
}

// Editor for known investments: funds or companies the firm has invested in.
// Each row is a name plus an optional source link. Blank rows are dropped on save.
function KnownInvestmentsInput({ value, onChange }) {
  function update(i, key, v) {
    onChange(value.map((row, j) => j === i ? { ...row, [key]: v } : row))
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {value.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6 }}>
          <input className="input" value={row.name ?? ''} placeholder="Fund or company name"
            onChange={e => update(i, 'name', e.target.value)} />
          <input className="input" value={row.url ?? ''} placeholder="Source link (optional)"
            onChange={e => update(i, 'url', e.target.value)} />
          <button type="button" className="btn btn-secondary btn-sm" aria-label="Remove investment"
            onClick={() => onChange(value.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div>
        <button type="button" className="btn btn-secondary btn-sm"
          onClick={() => onChange([...value, { name: '', url: '' }])}>+ Add investment</button>
      </div>
    </div>
  )
}

function KnownInvestmentsList({ items }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {items.map(inv => (
        <div key={inv.name}>
          {inv.name}
          {inv.url && (
            <> · <a href={inv.url} target="_blank" rel="noreferrer"
              style={{ color: 'var(--accent)', fontSize: 12 }}>source</a></>
          )}
        </div>
      ))}
    </div>
  )
}

// Reading order for the interest dimensions. These are structural dimension
// names, not taxonomy terms — the terms themselves always come from the API.
// Anything the taxonomy adds later falls through alphabetically at the end
// rather than vanishing from the display.
const INTEREST_DIMENSION_ORDER = [
  'Sector', 'Sub-Sector', 'Asset Class', 'Strategy',
  'Structure', 'Geography', 'Stage', 'Check Size',
]

function compareDimensions(a, b) {
  const ia = INTEREST_DIMENSION_ORDER.indexOf(a)
  const ib = INTEREST_DIMENSION_ORDER.indexOf(b)
  if (ia !== -1 && ib !== -1) return ia - ib
  if (ia !== -1) return -1
  if (ib !== -1) return 1
  return a.localeCompare(b)
}

// Write vocabularies for the interest record itself. These are enum columns on
// investor_interest — structural, like the dimension order above. The taxonomy
// terms that hang off a record always come from the API, never from here.
const INTEREST_STATUSES    = ['not_started', 'partial', 'enriched', 'needs_review', 'no_data_found']
const INTEREST_CONFIDENCES = ['high', 'medium', 'low']

// The two mandate branches. branch_label on a record comes from the API; this
// list exists so we can offer the branch a firm does not have yet.
const INTEREST_BRANCHES = [
  { mandate_type: 'DD', label: 'Direct Deal' },
  { mandate_type: 'FS', label: 'Fund / SPV' },
]
const CROSS_CUTTING_BRANCH = 'Cross-Cutting'

function branchLabelFor(mandateType) {
  return INTEREST_BRANCHES.find(b => b.mandate_type === mandateType)?.label ?? mandateType
}

// A term may sit on a record when its branch is the record's branch or when it
// is cross-cutting. The DB enforces this too — this only keeps the picker from
// offering a term that would be rejected.
function termAllowedOnBranch(termBranch, mandateType) {
  return termBranch === CROSS_CUTTING_BRANCH || termBranch === branchLabelFor(mandateType)
}

// The interest endpoints return DB trigger messages written for a person to
// read — which term is scoped to the other branch, which parent it implies,
// which exclusive term it collides with. Show them verbatim: a generic failure
// message would lose the only thing that tells the user how to fix the tag.
function apiErrorMessage(err) {
  return err?.response?.data?.error || err?.message || 'Request failed.'
}

const SORTABLE_COLS = {
  name:      { label: 'Firm Name',       fn: (a, b) => (a.name || '').localeCompare(b.name || '') },
  type:      { label: 'Type',            fn: (a, b) => ((a.firm_type ?? [])[0] || '').localeCompare((b.firm_type ?? [])[0] || '') },
  subtype:   { label: 'Investor Subtype',fn: (a, b) => (a.investor_subtype || '').localeCompare(b.investor_subtype || '') },
  hq:        { label: 'HQ',              fn: (a, b) => (a.hq_location || '').localeCompare(b.hq_location || '') },
  aum:       { label: 'AUM / Fund Size', fn: (a, b) => (a.aum_fund_size ?? 0) - (b.aum_fund_size ?? 0) },
  notes:     { label: 'Notes',           fn: (a, b) => (a.notes || '').localeCompare(b.notes || '') },
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatAUM(n) {
  if (n == null) return null
  if (n >= 1e12) return `$${(n / 1e12).toFixed(1)}T`
  if (n >= 1e9)  return `$${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6)  return `$${Math.round(n / 1e6)}M`
  return `$${Number(n).toLocaleString()}`
}

function todayStr() { return new Date().toISOString().slice(0, 10) }

// Single source of truth for the Investor Subtype dropdown: the API, which
// reads firm_subtype_options. Cached for the session by react-query.
function useSubtypeOptions() {
  const { data } = useQuery({
    queryKey: ['firm-subtype-options'],
    queryFn: () => api.get('/api/firms/subtype-options').then(r => r.data.data ?? []),
    staleTime: 30 * 60 * 1000,
  })
  return data ?? []
}

// Taxonomy v1.0 vocabulary. Terms are never hard-coded here — the API is the
// only source. parents_by_dimension gives the short (level <= 2) list the
// filter dropdown offers; expand() walks the full parent_code tree so picking
// a parent also matches firms tagged only on a descendant.
function useTaxonomy() {
  const { data } = useQuery({
    queryKey: ['taxonomy-terms'],
    queryFn: () => api.get('/api/taxonomy/terms').then(r => r.data.data ?? {}),
    staleTime: 60 * 60 * 1000,
  })

  const terms = useMemo(() => data?.terms ?? [], [data])
  const parentsByDimension = useMemo(() => data?.parents_by_dimension ?? {}, [data])

  const childrenByParent = useMemo(() => {
    const m = new Map()
    for (const t of terms) {
      if (!t.parent_code) continue
      const kids = m.get(t.parent_code)
      if (kids) kids.push(t.code)
      else m.set(t.parent_code, [t.code])
    }
    return m
  }, [terms])

  // Iterative walk to arbitrary depth — DD.HLTH must reach DD.HLTH.PROV and
  // anything below it. The visited guard keeps a malformed cycle from hanging.
  const expand = useCallback((code) => {
    const out = new Set()
    const stack = [code]
    while (stack.length) {
      const c = stack.pop()
      if (out.has(c)) continue
      out.add(c)
      for (const kid of (childrenByParent.get(c) ?? [])) stack.push(kid)
    }
    return out
  }, [childrenByParent])

  return { terms, parentsByDimension, expand }
}

// Flat interest index for list filtering. One row per interest record, so a
// firm holding both a DD and an FS record contributes two rows — merge them.
function useInterestIndex() {
  const { data } = useQuery({
    queryKey: ['firm-interest-index'],
    queryFn: () => api.get('/api/firms/interest-index').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })

  const rows = useMemo(() => data ?? [], [data])

  const byFirm = useMemo(() => {
    const m = new Map()
    for (const r of rows) {
      if (!r?.firm_id) continue
      let entry = m.get(r.firm_id)
      if (!entry) { entry = { codes: new Set(), branches: [] }; m.set(r.firm_id, entry) }
      for (const c of (r.codes ?? [])) entry.codes.add(c)
      if (r.mandate_type && !entry.branches.includes(r.mandate_type)) entry.branches.push(r.mandate_type)
    }
    return m
  }, [rows])

  return { byFirm }
}

// ── Shared UI ─────────────────────────────────────────────────────────────

function Modal({ title, onClose, width = 560, zIndex = 1000, children }) {
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: width,
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '16px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button className="btn-icon" onClick={onClose} style={{ fontSize: 16 }}>&#10005;</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function ErrorBanner({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
      borderRadius: 6, padding: '10px 14px', fontSize: 13, color: 'var(--danger-text)',
    }}>{msg}</div>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
    </svg>
  )
}

function DeleteConfirmModal({ name, onCancel, onConfirm, deleting }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1200, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)',
        borderRadius: 10, width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
      }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Delete {name}?</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.5 }}>
          Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{name}</strong>?
          This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{children || '—'}</div>
    </div>
  )
}

function SortHeader({ col, sortCol, sortDir, onSort, children }) {
  const active = sortCol === col
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(col)}>
      {children} {active ? (sortDir === 'asc' ? '▲' : '▼') : <span style={{ opacity: 0.25 }}>{'▲'}</span>}
    </th>
  )
}

function MultiSelectFilter({ label, options, selected, onToggle, badgeMap }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm"
        onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label} {selected.length > 0 && (
          <span style={{
            background: 'var(--accent)', color: '#fff', borderRadius: 8,
            padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
          }}>{selected.length}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 10,
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, marginTop: 4, minWidth: 180, maxHeight: 260, overflow: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
          onMouseLeave={() => setOpen(false)}>
          {options.map(o => {
            const checked = selected.includes(o)
            return (
              <label key={o} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                cursor: 'pointer', fontSize: 12,
                background: checked ? 'var(--accent-muted)' : 'transparent',
              }}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(o)}
                  style={{ accentColor: 'var(--accent)' }} />
                {badgeMap
                  ? <span className={`badge ${badgeMap[o] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{o}</span>
                  : <span className={`badge ${tagColor(o)}`} style={{ fontSize: 10 }}>{o}</span>
                }
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Sibling of MultiSelectFilter for options that carry a code/label pair and
// need dimension headings. MultiSelectFilter takes flat strings and renders
// each as a badge, so it cannot group — same styling, different data shape.
function GroupedMultiSelectFilter({ label, groups, selected, onToggle }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm"
        onClick={() => setOpen(o => !o)}
        style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label} {selected.length > 0 && (
          <span style={{
            background: 'var(--accent)', color: '#fff', borderRadius: 8,
            padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
          }}>{selected.length}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 10,
          background: 'var(--surface)', border: '1px solid var(--border-light)',
          borderRadius: 6, marginTop: 4, minWidth: 240, maxHeight: 320, overflow: 'auto',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}
          onMouseLeave={() => setOpen(false)}>
          {groups.length === 0 ? (
            <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
              No taxonomy terms available.
            </div>
          ) : groups.map(g => (
            <div key={g.name}>
              <div style={{
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-muted)',
                padding: '8px 12px 3px', borderTop: '1px solid var(--border)',
              }}>{g.name}</div>
              {g.options.map(o => {
                const checked = selected.includes(o.value)
                return (
                  <label key={o.value} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px',
                    cursor: 'pointer', fontSize: 12,
                    background: checked ? 'var(--accent-muted)' : 'transparent',
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => onToggle(o.value)}
                      style={{ accentColor: 'var(--accent)' }} />
                    <span title={o.value}>{o.label}</span>
                  </label>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Pagination bar ────────────────────────────────────────────────────────

function PaginationBar({ page, totalPages, pageStart, pageSize, totalCount, onPage }) {
  const from = totalCount === 0 ? 0 : pageStart + 1
  const to   = Math.min(pageStart + pageSize, totalCount)

  // Build page-number window (up to 7 slots with ellipsis)
  function pageNums() {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages = []
    pages.push(1)
    if (page > 4) pages.push('…')
    const start = Math.max(2, page - 2)
    const end   = Math.min(totalPages - 1, page + 2)
    for (let i = start; i <= end; i++) pages.push(i)
    if (page < totalPages - 3) pages.push('…')
    pages.push(totalPages)
    return pages
  }

  const btnBase = {
    minWidth: 30, height: 28, padding: '0 7px', borderRadius: 4,
    border: '1px solid var(--border)', cursor: 'pointer', fontSize: 12,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        Showing {from}–{to} of {totalCount} records
      </span>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <button
          onClick={() => onPage(p => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{ ...btnBase, background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
        >‹ Prev</button>

        {pageNums().map((n, i) =>
          n === '…'
            ? <span key={`e${i}`} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '0 4px' }}>…</span>
            : <button
                key={n}
                onClick={() => onPage(n)}
                style={{
                  ...btnBase,
                  background: n === page ? 'var(--accent)' : 'var(--surface-raised)',
                  color: n === page ? '#fff' : 'var(--text-secondary)',
                  fontWeight: n === page ? 700 : 400,
                  borderColor: n === page ? 'var(--accent)' : 'var(--border)',
                }}
              >{n}</button>
        )}

        <button
          onClick={() => onPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          style={{ ...btnBase, background: 'var(--surface-raised)', color: 'var(--text-secondary)' }}
        >Next ›</button>
      </div>
    </div>
  )
}

// ── Export helpers ────────────────────────────────────────────────────────

function exportFirms(rows, users, format) {
  const resolveOwners = (ids) => {
    if (!ids || !ids.length) return ''
    return ids.map(id => {
      const u = users.find(u => u.id === id || u.contact_id === id)
      return u ? u.name : null
    }).filter(Boolean).join(', ')
  }

  const data = rows.map(f => ({
    'Name':               f.name ?? '',
    'Firm Type':          (f.firm_type ?? []).join(', '),
    'Investor Subtype':   f.investor_subtype ?? '',
    'Principal Families': (f.principal_families ?? []).join('; '),
    'Known Investments':  (f.known_investments ?? []).map(i => i.name).join('; '),
    'HQ Location':        f.hq_location ?? '',
    'AUM/Fund Size':      f.aum_fund_size ?? '',
    'Website':            f.website ?? '',
    'Tags':               (f.tags ?? []).join(', '),
    'Relationship Owner': resolveOwners(f.relationship_owner ?? []),
    'Contacts':           (f.contacts ?? []).map(c => c.name).join(', '),
  }))

  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Firms')

  const filename = `RGA_Firms_${todayStr()}.${format === 'xlsx' ? 'xlsx' : 'csv'}`
  if (format === 'xlsx') {
    XLSX.writeFile(wb, filename)
  } else {
    XLSX.writeFile(wb, filename, { bookType: 'csv' })
  }
}

// ── Firm form ─────────────────────────────────────────────────────────────

export function FirmForm({ initial = {}, users, onSubmit, onCancel, submitting, error }) {
  const { firmTypes } = useRoleOptions()
  const subtypeOptions = useSubtypeOptions()
  const [form, setForm] = useState({
    name:                        initial.name               ?? '',
    firm_type:                   initial.firm_type          ?? [],
    investor_subtype:            initial.investor_subtype    ?? '',
    principal_families:          initial.principal_families  ?? [],
    known_investments:           initial.known_investments   ?? [],
    website:                     initial.website             ?? '',
    hq_location:                 initial.hq_location         ?? '',
    aum_fund_size:               initial.aum_fund_size != null ? String(initial.aum_fund_size) : '',
    tags:                       (initial.tags               ?? []).join(', '),
    relationship_owner:          initial.relationship_owner  ?? [],
    notes:                       initial.notes               ?? '',
  })
  const [err, setErr] = useState(null)
  // Soft gate: first Save on a family office with no family shows a warning;
  // the second click ("Save anyway") goes through and the firm is flagged.
  const [familyWarned, setFamilyWarned] = useState(false)
  const isFamilySubtype = FAMILY_SUBTYPES.includes(form.investor_subtype)
  const missingFamily = isFamilySubtype && form.principal_families.length === 0

  function set(k, v) {
    setForm(p => ({ ...p, [k]: v })); setErr(null)
    if (k === 'investor_subtype' || k === 'principal_families') setFamilyWarned(false)
  }

  function toggleType(t) {
    setForm(p => ({
      ...p,
      firm_type: p.firm_type.includes(t)
        ? p.firm_type.filter(x => x !== t)
        : [...p.firm_type, t],
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim())    return setErr('Name is required.')
    if (!form.firm_type.length) return setErr('At least one firm type is required.')
    if (missingFamily && !familyWarned) return setFamilyWarned(true)
    const payload = {
      name:      form.name.trim(),
      firm_type: form.firm_type,
      // '' clears the field — blank means "not researched", never a default value
      investor_subtype: form.investor_subtype || null,
      principal_families: form.principal_families,
      known_investments: form.known_investments
        .map(i => ({ name: (i.name ?? '').trim(), url: (i.url ?? '').trim() }))
        .filter(i => i.name)
        .map(i => i.url ? i : { name: i.name }),
    }
    if (form.website)            payload.website            = form.website.trim()
    if (form.hq_location)        payload.hq_location        = form.hq_location.trim()
    if (form.aum_fund_size)      payload.aum_fund_size      = parseFloat(form.aum_fund_size)
    if (form.tags.trim())        payload.tags               = form.tags.split(',').map(s => s.trim()).filter(Boolean)
    payload.relationship_owner = form.relationship_owner
    if (form.notes)              payload.notes              = form.notes.trim()
    try { await onSubmit(payload) }
    catch (err2) { setErr(err2.response?.data?.error || err2.message) }
  }

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ErrorBanner msg={err || error} />

      {/* Name */}
      <div className="input-group">
        <label className="input-label">Firm Name *</label>
        <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
          placeholder="e.g. Halcyon Endowment Fund" autoFocus />
      </div>

      {/* Firm Type */}
      <div className="input-group">
        <label className="input-label">Firm Type *</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {firmTypes.map(t => {
            const checked = form.firm_type.includes(t)
            return (
              <label key={t} style={{
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                fontSize: 12, padding: '5px 10px', borderRadius: 5,
                border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                background: checked ? 'var(--accent-muted)' : 'transparent',
                color: checked ? 'var(--text-primary)' : 'var(--text-secondary)',
                transition: 'all 0.12s',
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggleType(t)}
                  style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                {t}
              </label>
            )
          })}
        </div>
      </div>

      {/* Investor Subtype */}
      <div className="input-group">
        <label className="input-label">Investor Subtype</label>
        <select className="select" value={form.investor_subtype}
          onChange={e => set('investor_subtype', e.target.value)}>
          <option value="">— Not researched —</option>
          {subtypeOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Principal families: shown for family offices, and kept visible if a
          firm already has names but its subtype changed */}
      {(isFamilySubtype || form.principal_families.length > 0) && (
        <div className="input-group">
          <label className="input-label">
            {form.investor_subtype === 'Multi-Family Office'
              ? 'Families (founding family first)'
              : 'Family / Principal'}
          </label>
          <FamilyChipsInput
            value={form.principal_families}
            onChange={v => set('principal_families', v)}
            placeholder={form.investor_subtype === 'Multi-Family Office'
              ? 'e.g. Zuckerberg. Press Enter after each name'
              : 'e.g. Bloomberg (Michael Bloomberg). Press Enter to add'} />
        </div>
      )}

      {/* Website + HQ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Website</label>
          <input className="input" value={form.website} onChange={e => set('website', e.target.value)}
            placeholder="https://..." />
        </div>
        <div className="input-group">
          <label className="input-label">HQ Location</label>
          <input className="input" value={form.hq_location} onChange={e => set('hq_location', e.target.value)}
            placeholder="New York, NY" />
        </div>
      </div>

      {/* AUM */}
      <div className="input-group">
        <label className="input-label">AUM / Fund Size ($)</label>
        <input className="input" type="number" min="0" value={form.aum_fund_size}
          onChange={e => set('aum_fund_size', e.target.value)} placeholder="e.g. 4200000000" />
        {form.aum_fund_size !== '' && (
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
            = {formatAUM(parseFloat(form.aum_fund_size))}
          </div>
        )}
      </div>

      {/* Relationship Owner — searchable, individuals only */}
      <div className="input-group">
        <label className="input-label">Relationship Owner</label>
        <OwnerMultiField owners={users} value={form.relationship_owner}
          onChange={ids => setForm(p => ({ ...p, relationship_owner: ids }))} />
      </div>

      {/* Tags */}
      <div className="input-group">
        <label className="input-label">Tags</label>
        <input className="input" value={form.tags} onChange={e => set('tags', e.target.value)}
          placeholder="endowment, buyout, North America - comma-separated" />
      </div>

      {/* Known investments */}
      <div className="input-group">
        <label className="input-label">Known Investments</label>
        <KnownInvestmentsInput value={form.known_investments}
          onChange={v => set('known_investments', v)} />
      </div>

      {/* Notes */}
      <div className="input-group">
        <label className="input-label">Notes</label>
        <textarea className="textarea" rows={2} value={form.notes}
          onChange={e => set('notes', e.target.value)} placeholder="Internal notes..." />
      </div>

      {familyWarned && missingFamily && (
        <div role="alert" style={{ fontSize: 12, padding: '10px 12px', borderRadius: 6,
          border: '1px solid var(--warning, #d97706)', background: 'rgba(217, 119, 6, 0.08)',
          color: 'var(--text-primary)' }}>
          No family or principal listed. This firm will be flagged on the Dashboard until one is added.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8,
        borderTop: '1px solid var(--border)', marginTop: 4 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving...' : (familyWarned && missingFamily ? 'Save anyway' : 'Save')}
        </button>
      </div>
    </form>
  )
}

// ── Investment interest ───────────────────────────────────────────────────

// Term picker for the per-dimension "+" controls. Every term here comes from
// useTaxonomy() — nothing about the vocabulary is hard-coded. Default view is
// the level-1 and level-2 parents grouped by dimension with children behind a
// disclosure; 301 terms rendered flat would be unusable, so searching is the
// only path that goes flat, and it searches the whole valid list by label or
// code.
//
// `dimension` is optional. When set, the pool narrows to that dimension only
// and the groups become the parents the terms hang off — a sub-sector's parent
// sector lives in the Sector dimension and so is not itself in the pool, so it
// is drawn as a non-selectable grouping heading instead of being dropped along
// with its children. When unset the behaviour is the whole-branch view.
function TermPicker({ mandateType, branchLabel, dimension, terms, taggedCodes, busy, onPick, onClose }) {
  const [q, setQ]               = useState('')
  const [expanded, setExpanded] = useState(() => new Set())

  // Everything this record could legally carry: taggable, and on this branch
  // or cross-cutting. Already-tagged terms stay in the pool so a tagged parent
  // can still act as a disclosure for its untagged children, but they are not
  // selectable and never appear in search results.
  const pool = useMemo(
    () => (terms ?? []).filter(t =>
      t.taggable !== false &&
      termAllowedOnBranch(t.branch, mandateType) &&
      (!dimension || t.dimension === dimension)
    ),
    [terms, mandateType, dimension]
  )

  // Labels for grouping headings are looked up against the full vocabulary,
  // not the pool — the parent of a filtered term is usually outside it.
  const termsByCode = useMemo(() => {
    const m = new Map()
    for (const t of (terms ?? [])) m.set(t.code, t)
    return m
  }, [terms])

  const childrenOf = useMemo(() => {
    const m = new Map()
    for (const t of pool) {
      if (!t.parent_code) continue
      const kids = m.get(t.parent_code)
      if (kids) kids.push(t)
      else m.set(t.parent_code, [t])
    }
    return m
  }, [pool])

  const groups = useMemo(() => {
    if (dimension) {
      // Roots of the filtered pool: a term whose parent is also in the pool is
      // reached through that parent's disclosure, not listed again here.
      const inPool = new Set(pool.map(t => t.code))
      const byParent = new Map()
      for (const t of pool) {
        if (t.parent_code && inPool.has(t.parent_code)) continue
        const key = t.parent_code ?? ''
        const g = byParent.get(key)
        if (g) g.push(t)
        else byParent.set(key, [t])
      }
      const out = Array.from(byParent, ([parentCode, options]) => ({
        key: parentCode || dimension,
        name: parentCode ? (termsByCode.get(parentCode)?.label ?? parentCode) : dimension,
        parentCode,
        options,
      })).sort((a, b) =>
        (a.parentCode ? 1 : 0) - (b.parentCode ? 1 : 0) || a.name.localeCompare(b.name))
      // A lone heading conveys nothing the modal title has not already said —
      // every Sector hangs off the same mandate root — so name it for the
      // dimension. Several headings are real grouping (sub-sectors by sector).
      if (out.length === 1) { out[0].name = dimension; out[0].key = dimension }
      return out
    }
    const byDim = new Map()
    for (const t of pool) {
      if ((t.level ?? 1) > 2) continue
      const g = byDim.get(t.dimension)
      if (g) g.push(t)
      else byDim.set(t.dimension, [t])
    }
    return Array.from(byDim, ([name, options]) => ({ key: name, name, parentCode: '', options }))
      .sort((a, b) => compareDimensions(a.name, b.name))
  }, [pool, dimension, termsByCode])

  const needle  = q.trim().toLowerCase()
  const matches = useMemo(() => {
    if (!needle) return []
    return pool.filter(t =>
      !taggedCodes.has(t.code) &&
      ((t.label ?? '').toLowerCase().includes(needle) || t.code.toLowerCase().includes(needle))
    )
  }, [needle, pool, taggedCodes])

  function toggle(code) {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(code) ? next.delete(code) : next.add(code)
      return next
    })
  }

  const rowBtn = {
    background: 'none', border: 'none', padding: '2px 0', textAlign: 'left',
    fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer', flex: 1,
  }

  // A plain render helper, deliberately not a component: it recurses inside
  // the render pass and must never call a hook.
  function renderNode(t, depth) {
    const kids    = childrenOf.get(t.code) ?? []
    const open    = expanded.has(t.code)
    const tagged  = taggedCodes.has(t.code)
    return (
      <div key={t.code}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '3px 12px 3px ' + (12 + depth * 16) + 'px',
        }}>
          {kids.length > 0 ? (
            <button className="btn-icon" onClick={() => toggle(t.code)}
              title={open ? 'Collapse' : 'Expand'}
              style={{ fontSize: 10, width: 16, flexShrink: 0 }}>{open ? '▾' : '▸'}</button>
          ) : (
            <span style={{ width: 16, flexShrink: 0 }} />
          )}
          <button style={{ ...rowBtn, cursor: tagged ? 'default' : 'pointer', opacity: tagged ? 0.4 : 1 }}
            title={t.code} disabled={tagged || busy}
            onClick={() => onPick(t.code)}>{t.label}</button>
          {tagged && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>tagged</span>}
        </div>
        {open && kids.map(k => renderNode(k, depth + 1))}
      </div>
    )
  }

  return (
    <Modal title={dimension ? `Add ${dimension} tag - ${branchLabel}` : `Add tag - ${branchLabel}`}
      onClose={onClose} width={520} zIndex={1100}>
      <div style={{ padding: '16px 24px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input className="input" autoFocus placeholder="Search terms by label or code..."
          value={q} onChange={e => setQ(e.target.value)} />

        <div style={{
          border: '1px solid var(--border)', borderRadius: 6,
          maxHeight: 380, overflow: 'auto',
        }}>
          {needle ? (
            matches.length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
                No matching terms available for this branch.
              </div>
            ) : matches.map(t => (
              <div key={t.code} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px',
              }}>
                <button style={rowBtn} title={t.code} disabled={busy}
                  onClick={() => onPick(t.code)}>{t.label}</button>
                {!dimension && (
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>{t.dimension}</span>
                )}
              </div>
            ))
          ) : groups.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
              No taxonomy terms available.
            </div>
          ) : groups.map(g => (
            <div key={g.key}>
              <div style={{
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-muted)',
                padding: '8px 12px 3px', borderTop: '1px solid var(--border)',
              }}>{g.name}</div>
              {g.options.map(t => renderNode(t, 0))}
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Tags are added at high confidence. Expand a parent to reach its children.
        </div>
      </div>
    </Modal>
  )
}

// One tagged interest record — a firm may hold both a DD and an FS record.
// Owns only its own view state; every write is a handler passed down from
// InvestmentInterest, which is where the mutations live.
function InterestRecord({
  record, editing, firmName, terms, busy,
  onPatch, onDeleteRecord, onAddTag, onRemoveTag,
}) {
  const [notesDraft, setNotesDraft] = useState(record.notes ?? '')
  // One piece of state for the whole record: the dimension whose picker is
  // open, or null. A hook per dimension would put hooks inside the loop below.
  const [pickerDim, setPickerDim] = useState(null)

  const byDimension = useMemo(() => record.by_dimension ?? {}, [record])
  const taggedCodes = useMemo(() => {
    const s = new Set()
    for (const list of Object.values(byDimension)) {
      for (const t of (list ?? [])) s.add(t.code)
    }
    return s
  }, [byDimension])

  // Which dimensions this branch can carry at all, derived from the taxonomy
  // rather than listed here: every taggable term valid on this record's branch
  // contributes its dimension. A dimension the firm has no tags in yet still
  // has to appear in edit mode, or there is no way to reach it.
  const branchDimensions = useMemo(() => {
    const s = new Set()
    for (const t of (terms ?? [])) {
      if (t.taggable === false) continue
      if (!termAllowedOnBranch(t.branch, record.mandate_type)) continue
      if (t.dimension) s.add(t.dimension)
    }
    return Array.from(s)
  }, [terms, record.mandate_type])

  const taggedDimensions = Object.keys(byDimension).filter(d => (byDimension[d] ?? []).length > 0)

  // Read-only shows only what is tagged. Edit mode shows the whole branch, plus
  // anything already tagged that the derived list somehow misses.
  const dimensions = (editing
    ? Array.from(new Set([...branchDimensions, ...taggedDimensions]))
    : taggedDimensions
  ).sort(compareDimensions)

  const meta = [
    record.status,
    record.confidence && `${record.confidence} confidence`,
    record.tag_count != null && `${record.tag_count} tag${record.tag_count === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ')

  function confirmDelete() {
    const n    = record.tag_count ?? taggedCodes.size
    const tail = n === 0 ? 'It has no tags.'
      : n === 1 ? 'Its 1 tag goes with it.'
      : `Its ${n} tags go with it.`
    if (window.confirm(`Delete the ${record.branch_label} interest record for ${firmName}? ${tail}`)) {
      onDeleteRecord(record)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="badge badge-blue">{record.branch_label}</span>
        {meta && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{meta}</span>}
        {editing && (
          <button className="btn btn-ghost btn-sm" onClick={confirmDelete} disabled={busy}
            title="Delete this interest record and all of its tags"
            style={{ marginLeft: 'auto', color: 'var(--danger)' }}>
            Delete record
          </button>
        )}
      </div>

      {editing && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
          <Field label="Status">
            <select className="select" style={{ fontSize: 12 }} disabled={busy}
              value={record.status ?? ''}
              onChange={e => onPatch(record.interest_id, { status: e.target.value })}>
              {INTEREST_STATUSES.map(s => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </Field>
          <Field label="Confidence">
            <select className="select" style={{ fontSize: 12 }} disabled={busy}
              value={record.confidence ?? ''}
              onChange={e => onPatch(record.interest_id, { confidence: e.target.value })}>
              <option value="">— none —</option>
              {INTEREST_CONFIDENCES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Notes">
              <textarea className="textarea" rows={2} value={notesDraft} disabled={busy}
                placeholder="Enrichment notes..."
                onChange={e => setNotesDraft(e.target.value)}
                onBlur={() => {
                  if (notesDraft !== (record.notes ?? '')) onPatch(record.interest_id, { notes: notesDraft })
                }} />
            </Field>
          </div>
        </div>
      )}

      {dimensions.map(d => (
        <Field key={d} label={editing ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {d}
            <button className="btn-icon" disabled={busy}
              onClick={() => setPickerDim(d)}
              title={`Add ${d} tag`}
              style={{ fontSize: 12, lineHeight: 1 }}>+</button>
          </span>
        ) : d}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            {(byDimension[d] ?? []).length === 0 && (
              <span style={{ color: 'var(--text-muted)' }}>—</span>
            )}
            {(byDimension[d] ?? []).map(t => (
              <span key={t.code} className={`badge ${tagColor(t.dimension ?? d)}`}
                title={t.code}
                style={{
                  fontSize: 10, opacity: t.confidence === 'low' ? 0.45 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                {t.label}
                {editing && (
                  <button onClick={() => onRemoveTag(record.interest_id, t.code)} disabled={busy}
                    title={`Remove ${t.code}`}
                    style={{
                      background: 'none', border: 'none', padding: 0, margin: 0,
                      color: 'inherit', cursor: 'pointer', fontSize: 11, lineHeight: 1, opacity: 0.75,
                    }}>×</button>
                )}
              </span>
            ))}
          </div>
        </Field>
      ))}

      {!editing && record.notes && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {record.notes}
        </div>
      )}

      {pickerDim && (
        <TermPicker
          mandateType={record.mandate_type}
          branchLabel={record.branch_label}
          dimension={pickerDim}
          terms={terms}
          taggedCodes={taggedCodes}
          busy={busy}
          onPick={code => { setPickerDim(null); onAddTag(record.interest_id, code) }}
          onClose={() => setPickerDim(null)}
        />
      )}
    </div>
  )
}

// The Investment Interest section. Owns every interest mutation so that no
// hook is ever declared inside InterestRecord's dimension loop — the records
// get plain handlers instead. Read-only is the default; Edit flips the whole
// section, including the missing-branch create buttons.
function InvestmentInterest({ firmId, firmName, records, loading }) {
  const qc = useQueryClient()
  const { terms } = useTaxonomy()
  const [editing, setEditing] = useState(false)
  const [err, setErr]         = useState(null)

  // Both keys: the detail panel reads ['firm-interest', id], the list filter
  // reads ['firm-interest-index'].
  const onOk = useCallback(() => {
    setErr(null)
    qc.invalidateQueries({ queryKey: ['firm-interest', firmId] })
    qc.invalidateQueries({ queryKey: ['firm-interest-index'] })
  }, [qc, firmId])

  // Verbatim — the DB's taxonomy-rule messages are the whole point.
  const onFail = useCallback(e => setErr(apiErrorMessage(e)), [])

  const createRecordMutation = useMutation({
    mutationFn: mandate_type =>
      api.post(`/api/firms/${firmId}/interest`, { mandate_type, status: 'partial' }).then(r => r.data),
    onSuccess: onOk, onError: onFail,
  })

  const updateRecordMutation = useMutation({
    mutationFn: ({ interestId, patch }) =>
      api.patch(`/api/firms/${firmId}/interest/${interestId}`, patch).then(r => r.data),
    onSuccess: onOk, onError: onFail,
  })

  const deleteRecordMutation = useMutation({
    mutationFn: interestId =>
      api.delete(`/api/firms/${firmId}/interest/${interestId}`).then(r => r.data),
    onSuccess: onOk, onError: onFail,
  })

  const addTagMutation = useMutation({
    mutationFn: ({ interestId, termCode }) =>
      api.post(`/api/firms/${firmId}/interest/${interestId}/tags`,
        { term_code: termCode, confidence: 'high' }).then(r => r.data),
    onSuccess: onOk, onError: onFail,
  })

  const removeTagMutation = useMutation({
    mutationFn: ({ interestId, termCode }) =>
      api.delete(`/api/firms/${firmId}/interest/${interestId}/tags/${encodeURIComponent(termCode)}`)
        .then(r => r.data),
    onSuccess: onOk, onError: onFail,
  })

  const busy = createRecordMutation.isPending || updateRecordMutation.isPending ||
    deleteRecordMutation.isPending || addTagMutation.isPending || removeTagMutation.isPending

  const held   = new Set((records ?? []).map(r => r.mandate_type))
  const missing = INTEREST_BRANCHES.filter(b => !held.has(b.mandate_type))

  const addRecordButtons = missing.length > 0 && (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: records.length ? 4 : 8 }}>
      {missing.map(b => (
        <button key={b.mandate_type} className="btn btn-secondary btn-sm" disabled={busy}
          onClick={() => createRecordMutation.mutate(b.mandate_type)}>
          + Add {b.label} record
        </button>
      ))}
    </div>
  )

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
          Investment Interest
        </div>
        <button className="btn btn-secondary btn-sm"
          onClick={() => { setEditing(e => !e); setErr(null) }}>
          {editing ? 'Done' : 'Edit'}
        </button>
      </div>

      {err && (
        <div style={{ marginBottom: 10 }}><ErrorBanner msg={err} /></div>
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
      ) : records.length === 0 ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            No interest record yet — this firm has not been enriched under Taxonomy v1.0.
          </div>
          {editing && addRecordButtons}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {records.map(rec => (
              <InterestRecord
                key={rec.interest_id}
                record={rec}
                editing={editing}
                firmName={firmName}
                terms={terms}
                busy={busy}
                onPatch={(interestId, patch) => updateRecordMutation.mutate({ interestId, patch })}
                onDeleteRecord={r => deleteRecordMutation.mutate(r.interest_id)}
                onAddTag={(interestId, termCode) => addTagMutation.mutate({ interestId, termCode })}
                onRemoveTag={(interestId, termCode) => removeTagMutation.mutate({ interestId, termCode })}
              />
            ))}
          </div>
          {editing && addRecordButtons}
        </>
      )}
    </div>
  )
}

// ── Firm detail modal ─────────────────────────────────────────────────────

function FirmDetailModal({ firm, onClose, onUpdated, users }) {
  const navigate = useNavigate()
  const [editMode, setEditMode] = useState(false)
  const qc = useQueryClient()

  const { data: contactsData, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts', { firm_id: firm.firm_id }],
    queryFn: () => api.get('/api/contacts', { params: { firm_id: firm.firm_id } }).then(r => r.data.data ?? []),
  })
  const firmContacts = contactsData ?? []

  const [intPage, setIntPage] = useState(10)
  const { data: interactionsData, isLoading: interactionsLoading } = useQuery({
    queryKey: ['firm-interactions', firm.firm_id],
    queryFn: () => api.get(`/api/firms/${firm.firm_id}/interactions`).then(r => r.data.data ?? []),
  })
  const firmInteractions = interactionsData ?? []

  // Taxonomy v1.0 interest. Declared above the editMode early return so the
  // hook order stays stable across both renders. Writes live in
  // InvestmentInterest, which invalidates this key on every success.
  const { data: interestData, isLoading: interestLoading } = useQuery({
    queryKey: ['firm-interest', firm.firm_id],
    queryFn: () => api.get(`/api/firms/${firm.firm_id}/interest`).then(r => r.data.data ?? []),
  })
  const firmInterest = interestData ?? []

  const updateMutation = useMutation({
    mutationFn: payload => api.patch(`/api/firms/${firm.firm_id}`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firms'] })
      setEditMode(false)
      onUpdated?.()
    },
  })

  const ownerNames = (firm.relationship_owner ?? []).map(id => {
    const u = (users ?? []).find(u => u.id === id || u.contact_id === id)
    return u ? u.name : null
  }).filter(Boolean).join(', ') || null

  if (editMode) {
    return (
      <Modal title={`Edit - ${firm.name}`} onClose={onClose} width={580}>
        <FirmForm
          initial={firm}
          users={users}
          onSubmit={updateMutation.mutateAsync}
          onCancel={() => setEditMode(false)}
          submitting={updateMutation.isPending}
          error={updateMutation.error?.response?.data?.error}
        />
      </Modal>
    )
  }

  return (
    <Modal title={firm.name} onClose={onClose} width={580}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{firm.name}</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {(firm.firm_type ?? []).map(t => (
                <span key={t} className={`badge ${FIRM_TYPE_BADGE[t] ?? 'badge-gray'}`}>{t}</span>
              ))}
              {firm.investor_subtype && (
                <span className="badge badge-gray">{firm.investor_subtype}</span>
              )}
              {firm.needs_family && <MissingFamilyBadge size={11} />}
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditMode(true)}>Edit</button>
        </div>

        <div className="divider" />

        {/* Detail grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
          <Field label="Investor Subtype">{firm.investor_subtype}</Field>
          {(FAMILY_SUBTYPES.includes(firm.investor_subtype) || (firm.principal_families ?? []).length > 0) && (
            <Field label={firm.investor_subtype === 'Multi-Family Office' ? 'Families' : 'Family / Principal'}>
              {(firm.principal_families ?? []).length
                ? firm.principal_families.join(', ')
                : <span style={{ color: 'var(--text-muted)' }}>Not yet known</span>}
            </Field>
          )}
          <Field label="HQ Location">{firm.hq_location}</Field>
          <Field label="AUM / Fund Size">{formatAUM(firm.aum_fund_size)}</Field>
          <Field label="Website">
            {firm.website
              ? <a href={firm.website} target="_blank" rel="noreferrer"
                  style={{ color: 'var(--accent)' }}>{firm.website}</a>
              : null}
          </Field>
          <Field label="Relationship Owner">{ownerNames}</Field>
          {(firm.tags ?? []).length > 0 && (
            <Field label="Tags">
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {firm.tags.map(t => <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 10 }}>{t}</span>)}
              </div>
            </Field>
          )}
          {firm.notes && <Field label="Notes">{firm.notes}</Field>}
        </div>

        {(firm.known_investments ?? []).length > 0 && (
          <Field label="Known Investments">
            <KnownInvestmentsList items={firm.known_investments} />
          </Field>
        )}

        {/* Investment interest — Taxonomy v1.0, read-only until Edit */}
        <div className="divider" />
        <InvestmentInterest
          firmId={firm.firm_id}
          firmName={firm.name}
          records={firmInterest}
          loading={interestLoading}
        />

        {/* Events attended, directly or through the firm's people */}
        <div className="divider" />
        <EventsAttended firmId={firm.firm_id} />
        <VenturesLinked firmId={firm.firm_id} divider />

        {/* Associated contacts */}
        <div className="divider" />
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Contacts ({firmContacts.length})
          </div>
          {contactsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <div className="spinner" />
            </div>
          ) : firmContacts.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No contacts linked to this firm.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {firmContacts.map(c => (
                <div key={c.contact_id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '9px 12px', background: 'var(--surface-raised)',
                  borderRadius: 6, border: '1px solid var(--border)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                      onClick={() => {
                        if (window.confirm(`Go to ${c.name}? This will take you to the Contacts page.`)) {
                          navigate(`/individuals?open=${c.contact_id}`)
                        }
                      }}
                      title={`Open ${c.name}`}>
                      {c.name}
                    </div>
                    {/* Their title at this firm; their other firms are not mentioned here. */}
                    {(() => {
                      const title = (c.firms ?? []).find(f => f.firm_id === firm.firm_id)?.title_role ?? c.title_role
                      return title && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{title}</div>
                    })()}
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {(c.contact_type ?? []).slice(0, 2).map(t => (
                      <span key={t} className={`badge ${CONTACT_TYPE_BADGE[t] ?? 'badge-gray'}`}
                        style={{ fontSize: 10 }}>{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="divider" />
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
            Interactions {!interactionsLoading && `(${firmInteractions.length})`}
          </div>
          {interactionsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <div className="spinner" />
            </div>
          ) : firmInteractions.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No interactions logged for this firm.</div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {firmInteractions.slice(0, intPage).map(i => {
                  const d   = new Date(i.date)
                  const fmt = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  const src = i.source === 'Gmail Sync' ? '✉' : i.source === 'Calendar Sync' ? '📅' : '✏'
                  const names = i.contact_names ?? []
                  const nameStr = names.length <= 2
                    ? names.join(', ')
                    : `${names.slice(0, 2).join(', ')} +${names.length - 2} more`
                  const title = (i.subject_title ?? '(no subject)')
                  const titleShort = title.length > 55 ? title.slice(0, 55) + '…' : title

                  return (
                    <div
                      key={i.interaction_id}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10,
                        padding: '8px 10px', borderRadius: 6,
                        border: '1px solid var(--border)', background: 'var(--surface-raised)',
                        cursor: 'default',
                      }}
                    >
                      {/* Source icon */}
                      <span title={i.source} style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>{src}</span>

                      {/* Main content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}
                            title={title}>{titleShort}</span>
                          {i.mandate_name && (
                            <span className="badge badge-blue" style={{ fontSize: 10, flexShrink: 0 }}>
                              {i.mandate_name}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8 }}>
                          <span>{fmt}</span>
                          {nameStr && <span>· {nameStr}</span>}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
              {firmInteractions.length > intPage && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 8, width: '100%' }}
                  onClick={() => setIntPage(p => p + 10)}
                >
                  Show more ({firmInteractions.length - intPage} remaining)
                </button>
              )}
            </>
          )}
        </div>

      </div>
    </Modal>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function Firms() {
  const { allFirmTypes } = useRoleOptions()
  const [search, setSearch]           = useState('')
  const [typeFilter, setTypeFilter]   = useState([])
  const [subtypeFilter, setSubtypeFilter] = useState([])
  const [missingFamilyOnly, setMissingFamilyOnly] = useState(false)
  const [taxonomyFilter, setTaxonomyFilter] = useState([])
  const [tagFilter, setTagFilter]     = useState([])
  const [ownerFilter, setOwnerFilter] = useState('')
  const [aumFilter, setAumFilter]     = useState('')
  const [sortCol, setSortCol]         = useState('name')
  const [sortDir, setSortDir]         = useState('asc')
  const [page, setPage]               = useState(1)
  const [pageSize, setPageSize]       = useState(20)
  const [selected, setSelected]       = useState(null)
  const [createOpen, setCreate]       = useState(false)
  const [toDelete, setToDelete]       = useState(null)
  // ── merge / bulk-delete state ────────────────────────────────────────────
  const [checkedIds, setCheckedIds]   = useState(new Set())
  const [mergeOpen, setMergeOpen]     = useState(false)
  const [mergePrimaryId, setMergePrimaryId] = useState(null)
  const [mergeError, setMergeError]   = useState(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting]     = useState(false)
  const [bulkDeleteResult, setBulkDeleteResult] = useState(null) // { succeeded, failed }
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()

  const { data: firmsData, isLoading } = useQuery({
    queryKey: ['firms'],
    queryFn: () => api.get('/api/firms').then(r => r.data.data ?? []),
  })
  const subtypeOptions = useSubtypeOptions()
  const { parentsByDimension, expand } = useTaxonomy()
  const { byFirm: interestByFirm } = useInterestIndex()
  const { data: usersData } = useQuery({
    queryKey: ['relationship-owners'],
    queryFn: () => api.get('/api/users/relationship-owners').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })

  const firms = useMemo(() => firmsData ?? [], [firmsData])
  const users = useMemo(() => usersData ?? [], [usersData])

  const allTags = useMemo(() => {
    const s = new Set()
    for (const f of firms) { for (const t of (f.tags ?? [])) s.add(t) }
    return Array.from(s).sort()
  }, [firms])

  // Dropdown offers only the parent terms the API nominates per dimension —
  // never the full 301-row term list.
  const taxonomyGroups = useMemo(() => (
    Object.entries(parentsByDimension)
      .map(([dimension, opts]) => ({
        name: dimension,
        options: (opts ?? []).map(t => ({ value: t.code, label: t.label })),
      }))
      .filter(g => g.options.length > 0)
      .sort((a, b) => compareDimensions(a.name, b.name))
  ), [parentsByDimension])

  // Union of every selected term and its descendants; selections are OR.
  const taxonomyCodeSet = useMemo(() => {
    const s = new Set()
    for (const code of taxonomyFilter) {
      for (const c of expand(code)) s.add(c)
    }
    return s
  }, [taxonomyFilter, expand])

  const ownerName = useCallback((ids) => {
    const arr = Array.isArray(ids) ? ids : (ids ? [ids] : [])
    if (!arr.length) return '—'
    const names = arr.map(id => {
      const u = users.find(u => u.id === id || u.contact_id === id)
      return u ? u.name : null
    }).filter(Boolean)
    if (!names.length) return '—'
    if (names.length <= 2) return names.join(', ')
    return names.slice(0, 2).join(', ') + ` +${names.length - 2} more`
  }, [users])

  function toggleTypeFilter(t) { setTypeFilter(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]) }
  function toggleSubtypeFilter(t) { setSubtypeFilter(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]) }
  function toggleTagFilter(t) { setTagFilter(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]) }
  function toggleTaxonomyFilter(c) { setTaxonomyFilter(p => p.includes(c) ? p.filter(x => x !== c) : [...p, c]) }

  const hasFilters = Boolean(missingFamilyOnly || search || typeFilter.length || subtypeFilter.length || taxonomyFilter.length || tagFilter.length || ownerFilter || aumFilter)
  const activeFilterCount = (missingFamilyOnly ? 1 : 0) + (typeFilter.length ? 1 : 0) + (subtypeFilter.length ? 1 : 0) + (taxonomyFilter.length ? 1 : 0) + (tagFilter.length ? 1 : 0) + (ownerFilter ? 1 : 0) + (aumFilter ? 1 : 0)

  function clearAllFilters() {
    setSearch(''); setTypeFilter([]); setSubtypeFilter([]); setMissingFamilyOnly(false); setTaxonomyFilter([]); setTagFilter([]); setOwnerFilter(''); setAumFilter('')
    setPage(1)
  }

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  const filtered = useMemo(() => {
    let list = firms.filter(f => {
      if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false
      if (typeFilter.length && !typeFilter.some(t => f.firm_type?.includes(t))) return false
      if (subtypeFilter.length && !subtypeFilter.some(t =>
        t === UNCLASSIFIED ? !f.investor_subtype : f.investor_subtype === t)) return false
      if (missingFamilyOnly && !f.needs_family) return false
      if (taxonomyFilter.length) {
        const entry = interestByFirm.get(f.firm_id)
        if (!entry) return false
        let hit = false
        for (const c of entry.codes) { if (taxonomyCodeSet.has(c)) { hit = true; break } }
        if (!hit) return false
      }
      if (tagFilter.length && !tagFilter.some(t => (f.tags ?? []).includes(t))) return false
      if (ownerFilter && !(f.relationship_owner ?? []).includes(ownerFilter)) return false
      if (aumFilter) {
        const range = AUM_RANGES.find(r => r.label === aumFilter)
        if (range && !range.test(f.aum_fund_size)) return false
      }
      return true
    })

    const sortFn = SORTABLE_COLS[sortCol]?.fn
    if (sortFn) {
      list = [...list].sort((a, b) => sortDir === 'asc' ? sortFn(a, b) : sortFn(b, a))
    }
    return list
  }, [firms, search, typeFilter, subtypeFilter, missingFamilyOnly, taxonomyFilter, taxonomyCodeSet, interestByFirm, tagFilter, ownerFilter, aumFilter, sortCol, sortDir])

  // Reset to page 1 whenever filters or sort change
  useEffect(() => { setPage(1) }, [search, typeFilter, subtypeFilter, taxonomyFilter, tagFilter, ownerFilter, aumFilter, sortCol, sortDir])

  // Deep link from the Dashboard's family-office card: ?missing_family=1
  useEffect(() => {
    if (searchParams.get('missing_family') === '1') setMissingFamilyOnly(true)
  }, [searchParams])

  // Deep link from other pages (e.g. Partners' hot Firm links): ?open=<firm_id>
  // auto-opens that firm's detail modal once its data has loaded.
  useEffect(() => {
    const openId = searchParams.get('open')
    if (!openId || !firms.length) return
    const match = firms.find(f => f.firm_id === openId)
    if (match) setSelected(match)
  }, [searchParams, firms])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage   = Math.min(page, totalPages)
  const pageStart  = (safePage - 1) * pageSize        // 0-based
  const paginated  = filtered.slice(pageStart, pageStart + pageSize)

  const createMutation = useMutation({
    mutationFn: payload => api.post('/api/firms', payload).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['firms'] }); setCreate(false) },
  })

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/firms/${id}`).then(r => r.data),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['firms'] })
      setToDelete(null)
      setSelected(prev => prev?.firm_id === id ? null : prev)
    },
  })

  const mergeMutation = useMutation({
    mutationFn: ({ primary_id, merge_ids }) =>
      api.post('/api/firms/merge', { primary_id, merge_ids }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['firms'] })
      setCheckedIds(new Set())
      setMergeOpen(false)
      setMergeError(null)
    },
    onError: err => setMergeError(err.response?.data?.error || err.message),
  })

  // ── checkbox helpers ─────────────────────────────────────────────────────
  function toggleCheck(id, e) {
    e.stopPropagation()
    setCheckedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function toggleCheckAll() {
    const pageIds = paginated.map(f => f.firm_id)
    const allPageChecked = pageIds.every(id => checkedIds.has(id))
    if (allPageChecked) {
      setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.delete(id)); return n })
    } else {
      setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => n.add(id)); return n })
    }
  }

  const checkedRecords = useMemo(
    () => firms.filter(f => checkedIds.has(f.firm_id)),
    [firms, checkedIds]
  )

  function openMerge() {
    // Default primary = oldest created_at among checked
    const sorted = [...checkedRecords].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    )
    setMergePrimaryId(sorted[0]?.firm_id ?? null)
    setMergeError(null)
    setMergeOpen(true)
  }

  async function bulkDeleteFirms() {
    setBulkDeleting(true)
    setBulkDeleteResult(null)
    let succeeded = 0, failed = 0
    for (const id of checkedIds) {
      try {
        await api.delete(`/api/firms/${id}`)
        succeeded++
      } catch {
        failed++
      }
    }
    await qc.invalidateQueries({ queryKey: ['firms'] })
    setCheckedIds(new Set())
    setBulkDeleting(false)
    setBulkDeleteOpen(false)
    if (failed > 0) setBulkDeleteResult({ succeeded, failed })
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Firms</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isLoading ? 'Loading...' : `${firms.length} firm${firms.length !== 1 ? 's' : ''}`}
            {checkedIds.size > 0 && (
              <button
                onClick={() => setCheckedIds(new Set())}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  fontSize: 12, color: 'var(--text-secondary)',
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                Clear {checkedIds.size} selected
              </button>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {checkedIds.size >= 2 && (
            <button className="btn btn-secondary" onClick={openMerge}
              style={{ fontWeight: 600 }}>
              Merge Selected ({checkedIds.size})
            </button>
          )}
          {checkedIds.size >= 1 && (
            <button className="btn btn-danger" onClick={() => { setBulkDeleteResult(null); setBulkDeleteOpen(true) }}>
              Delete Selected ({checkedIds.size})
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => exportFirms(filtered, users, 'csv')}>Export CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={() => exportFirms(filtered, users, 'xlsx')}>Export Excel</button>
          <button className="btn btn-primary" onClick={() => setCreate(true)}>+ New Firm</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by name..."
          value={search} onChange={e => setSearch(e.target.value)} />

        <MultiSelectFilter label="Firm Type" options={allFirmTypes}
          selected={typeFilter} onToggle={toggleTypeFilter} badgeMap={FIRM_TYPE_BADGE} />

        <MultiSelectFilter label="Investor Subtype"
          options={[...subtypeOptions.map(o => o.value), UNCLASSIFIED]}
          selected={subtypeFilter} onToggle={toggleSubtypeFilter} />

        <button type="button" onClick={() => { setMissingFamilyOnly(v => !v); setPage(1) }}
          className={`btn btn-sm ${missingFamilyOnly ? 'btn-primary' : 'btn-secondary'}`}
          style={{ fontSize: 12 }} aria-pressed={missingFamilyOnly}>
          Missing family{missingFamilyOnly ? ' ✓' : ''}
        </button>

        <GroupedMultiSelectFilter label="Investment Interest"
          groups={taxonomyGroups}
          selected={taxonomyFilter} onToggle={toggleTaxonomyFilter} />

        {allTags.length > 0 && (
          <MultiSelectFilter label="Tags" options={allTags}
            selected={tagFilter} onToggle={toggleTagFilter} />
        )}

        <select className="select" style={{ maxWidth: 180, fontSize: 12 }} value={ownerFilter}
          onChange={e => setOwnerFilter(e.target.value)}>
          <option value="">All owners</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>

        <select className="select" style={{ maxWidth: 160, fontSize: 12 }} value={aumFilter}
          onChange={e => setAumFilter(e.target.value)}>
          <option value="">AUM range...</option>
          {AUM_RANGES.map(r => <option key={r.label} value={r.label}>{r.label}</option>)}
        </select>

        {hasFilters && (
          <button className="btn btn-ghost btn-sm" onClick={clearAllFilters}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Clear all{activeFilterCount > 0 && (
              <span style={{
                background: 'var(--accent)', color: '#fff', borderRadius: 8,
                padding: '0 5px', fontSize: 10, fontWeight: 700, lineHeight: '16px',
              }}>{activeFilterCount}</span>
            )}
          </button>
        )}

        {filtered.length !== firms.length && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {filtered.length} of {firms.length}
          </span>
        )}
      </div>

      {/* Per-page toggle */}
      {!isLoading && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span>Per page:</span>
          {[20, 50].map(n => (
            <button key={n} onClick={() => { setPageSize(n); setPage(1) }} style={{
              padding: '3px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
              border: '1px solid var(--border)',
              background: pageSize === n ? 'var(--accent)' : 'var(--surface-raised)',
              color: pageSize === n ? '#fff' : 'var(--text-secondary)',
              fontWeight: pageSize === n ? 600 : 400,
            }}>{n}</button>
          ))}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner spinner-lg" />
        </div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36, paddingRight: 0 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    checked={paginated.length > 0 && paginated.every(f => checkedIds.has(f.firm_id))}
                    ref={el => { if (el) { const pids = paginated.map(f => f.firm_id); el.indeterminate = pids.some(id => checkedIds.has(id)) && !pids.every(id => checkedIds.has(id)) } }}
                    onChange={toggleCheckAll}
                    title="Select all visible"
                  />
                </th>
                <SortHeader col="name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Firm Name</SortHeader>
                <SortHeader col="type" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Type</SortHeader>
                <SortHeader col="subtype" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Investor Subtype</SortHeader>
                <SortHeader col="hq" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>HQ</SortHeader>
                <SortHeader col="aum" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>AUM / Fund Size</SortHeader>
                <th>Owner</th>
                <th>Tags</th>
                <th>Individuals</th>
                <SortHeader col="notes" sortCol={sortCol} sortDir={sortDir} onSort={handleSort}>Notes</SortHeader>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={11}>
                  <div className="empty-state" style={{ padding: '40px 20px' }}>
                    <h3>{hasFilters ? 'No firms match your filters' : 'No firms yet'}</h3>
                    <p>{!hasFilters && 'Create your first firm to get started.'}</p>
                  </div>
                </td></tr>
              ) : (
                paginated.map(f => (
                  <tr
                    key={f.firm_id}
                    style={{
                      cursor: 'pointer',
                      background: checkedIds.has(f.firm_id) ? 'var(--accent-muted)' : undefined,
                    }}
                    onClick={() => setSelected(f)}
                  >
                    <td style={{ width: 36, paddingRight: 0 }} onClick={e => toggleCheck(f.firm_id, e)}>
                      <input
                        type="checkbox"
                        checked={checkedIds.has(f.firm_id)}
                        onChange={e => toggleCheck(f.firm_id, e)}
                        onClick={e => e.stopPropagation()}
                        style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ fontWeight: 500 }}>{f.name}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(f.firm_type ?? []).map(t => (
                          <span key={t} className={`badge ${FIRM_TYPE_BADGE[t] ?? 'badge-gray'}`}
                            style={{ fontSize: 10 }}>{t}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      {f.investor_subtype
                        ? <span className="badge badge-gray" style={{ fontSize: 10 }}>{f.investor_subtype}</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      }
                      {f.needs_family && <div style={{ marginTop: 4 }}><MissingFamilyBadge /></div>}
                      {(f.principal_families ?? []).length > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
                          {f.principal_families.join(', ')}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.hq_location ?? '—'}</td>
                    <td style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                      {formatAUM(f.aum_fund_size) ?? '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {ownerName(f.relationship_owner)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(f.tags ?? []).slice(0, 3).map(t => (
                          <span key={t} className={`badge ${tagColor(t)}`} style={{ fontSize: 10 }}>{t}</span>
                        ))}
                        {(f.tags ?? []).length > 3 && (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            +{f.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 200 }}>
                      {(() => {
                        const cs = f.contacts ?? []
                        if (cs.length === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>
                        if (cs.length <= 3) return cs.map(c => c.name).join(', ')
                        return (
                          <>
                            {cs.slice(0, 2).map(c => c.name).join(', ')}
                            <span style={{ color: 'var(--text-muted)' }}> +{cs.length - 2} more</span>
                          </>
                        )
                      })()}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', maxWidth: 180 }}>
                      {f.notes
                        ? <span title={f.notes}>{f.notes.length > 50 ? f.notes.slice(0, 50) + '...' : f.notes}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>
                      }
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ width: 36, paddingRight: 8 }}>
                      <button
                        onClick={() => setToDelete(f)}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--text-muted)', padding: '3px 5px', borderRadius: 4,
                          display: 'inline-flex', alignItems: 'center',
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        title="Delete firm"
                      ><TrashIcon /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination bar */}
      {!isLoading && filtered.length > 0 && (
        <PaginationBar
          page={safePage}
          totalPages={totalPages}
          pageStart={pageStart}
          pageSize={pageSize}
          totalCount={filtered.length}
          onPage={setPage}
        />
      )}

      {/* Create modal */}
      {createOpen && (
        <Modal title="New Firm" onClose={() => setCreate(false)}>
          <FirmForm
            users={users}
            onSubmit={createMutation.mutateAsync}
            onCancel={() => setCreate(false)}
            submitting={createMutation.isPending}
            error={createMutation.error?.response?.data?.error}
          />
        </Modal>
      )}

      {/* Detail modal */}
      {selected && (
        <FirmDetailModal
          firm={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => setSelected(null)}
          users={users}
        />
      )}

      {/* Delete confirmation */}
      {toDelete && (
        <DeleteConfirmModal
          name={toDelete.name}
          onCancel={() => setToDelete(null)}
          onConfirm={() => deleteMutation.mutate(toDelete.firm_id)}
          deleting={deleteMutation.isPending}
        />
      )}

      {/* Merge modal */}
      {mergeOpen && checkedRecords.length >= 2 && (
        <MergeModal
          records={checkedRecords}
          entityType="firm"
          primaryId={mergePrimaryId}
          onPrimaryChange={setMergePrimaryId}
          onConfirm={() => mergeMutation.mutate({
            primary_id: mergePrimaryId,
            merge_ids:  checkedRecords.map(f => f.firm_id).filter(id => id !== mergePrimaryId),
          })}
          onCancel={() => { setMergeOpen(false); setMergeError(null) }}
          merging={mergeMutation.isPending}
          error={mergeError}
        />
      )}

      {/* Bulk-delete confirmation modal */}
      {bulkDeleteOpen && (
        <div
          onClick={e => e.target === e.currentTarget && !bulkDeleting && setBulkDeleteOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1200, padding: 16,
          }}
        >
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border-light)',
            borderRadius: 10, width: '100%', maxWidth: 460,
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: '24px',
          }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
              Delete {checkedIds.size} Firm{checkedIds.size !== 1 ? 's' : ''}?
            </h2>

            {/* Name list — up to 10 */}
            <ul style={{ margin: '0 0 14px', padding: '0 0 0 18px', fontSize: 13, color: 'var(--text-primary)' }}>
              {checkedRecords.slice(0, 10).map(f => (
                <li key={f.firm_id} style={{ marginBottom: 2 }}>{f.name}</li>
              ))}
              {checkedRecords.length > 10 && (
                <li style={{ color: 'var(--text-secondary)' }}>
                  …and {checkedRecords.length - 10} more
                </li>
              )}
            </ul>

            {/* Warning */}
            <div style={{
              background: 'var(--danger-muted)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 6, padding: '10px 14px', fontSize: 12,
              color: 'var(--danger-text)', marginBottom: 20, lineHeight: 1.5,
            }}>
              This will also remove these firms from any associated contacts' firm lists
              and pipeline entries. This action cannot be undone.
            </div>

            {/* Partial-failure result */}
            {bulkDeleteResult && (
              <div style={{
                background: 'var(--danger-muted)', borderRadius: 6,
                padding: '8px 12px', fontSize: 12, color: 'var(--danger-text)', marginBottom: 14,
              }}>
                {bulkDeleteResult.succeeded} deleted, {bulkDeleteResult.failed} failed.
                The failed records may have dependent data — delete them individually.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={bulkDeleteFirms} disabled={bulkDeleting}>
                {bulkDeleting ? 'Deleting…' : `Delete ${checkedIds.size} Firm${checkedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
